import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import BatchTradeDialog, { tradeTypeLabel } from "@/components/benchmark/BatchTradeDialog";
import FundPicker from "@/components/benchmark/FundPicker";
import { useRangeFunds } from "@/components/benchmark/rangeFunds";
import { snapshotMmFundDurations } from "./mmDurationSnapshot";

import { tradeDirectionVariant, tradeDirectionHint } from "@/lib/tradeDirection";
interface Trade {
  id: string; trade_date: string; fund_id: string; direction: string;
  portfolio_1_included: boolean; portfolio_1_pct: number;
  fund_name_override: string | null; notes: string | null; created_at: string; trade_type: string | null; batch_id: string | null;
}
interface DurSnap { trade_id: string | null; weighted_avg_duration: number | null; snapshot_at: string; }

const P = 1;

export default function MmTradeLog() {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    trade_date: new Date().toISOString().split("T")[0],
    fund_id: "",
    direction: "BUY" as "BUY" | "SELL",
    portfolio_1_included: true, portfolio_1_pct: 0,
    notes: "",
  });

  const { data: trades } = useQuery({
    queryKey: ["ba-mm-trades"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_mm_trades").select("*").order("trade_date", { ascending: false });
      if (error) throw error;
      return data as Trade[];
    },
  });
  // Selection is resolved from ba_mm_cbam_fund_weights, never from is_current.
  //
  // is_current is derived by ba_fund_has_active_weight(), which looks ONLY at the P, R and Dist
  // weight tables — ba_mm_cbam_fund_weights is not consulted and carries no trg_sync_is_current
  // trigger. So the flag has never described the MM book: as of 2026-08-10, 6 of the 8 funds
  // holding live MM weights (Sterling Liquidity 15%, UK Money Market 15%, Money (UK) 14.21%,
  // Cash D 14%, Cash W 14%, Cash Trust 13.05%) read is_current = false, and the old
  // `.eq("is_current", true)` filter hid them — roughly 85% of the book by weight — leaving only
  // Short Term Money Market and Cash tradeable. This is a correctness fix, not only a buy-back one.
  const { selectable: funds, all: allFunds } = useRangeFunds("ba_mm_cbam_fund_weights");
  const { data: durSnaps } = useQuery({
    queryKey: ["ba-mm-duration-snapshots"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_mm_fund_duration_snapshots" as any)
        .select("trade_id, weighted_avg_duration, snapshot_at")
        .not("trade_id", "is", null);
      if (error) throw error;
      return ((data || []) as unknown) as DurSnap[];
    },
  });

  const addTradeMutation = useMutation({
    mutationFn: async () => {
      if (!form.fund_id) throw new Error("Please select a fund");
      const { data: inserted, error } = await supabase.from("ba_mm_trades").insert(form).select("id").single();
      if (error) throw error;
      if (form.portfolio_1_included) {
        // Weight rows carry the TRADE date, not today's — same defect as the P, R and Dist
        // single-trade paths. MM holds the deepest history in the estate (back to 2019), so
        // mis-dating a position change here is the most damaging of the four.
        const effectiveDate = form.trade_date;
        const sign = form.direction === "BUY" ? 1 : -1;
        // Weight as of the trade date, so a back-dated trade is not computed from its own future.
        const { data: existing } = await supabase.from("ba_mm_cbam_fund_weights").select("weight_pct").eq("fund_id", form.fund_id).eq("portfolio", P).lte("effective_date", effectiveDate).order("effective_date", { ascending: false }).limit(1);
        const current = Number(existing?.[0]?.weight_pct || 0);
        const next = Math.max(0, current + sign * Number(form.portfolio_1_pct || 0));
        await supabase.from("ba_mm_cbam_fund_weights").upsert(
          { fund_id: form.fund_id, portfolio: P, weight_pct: next, effective_date: effectiveDate },
          { onConflict: "fund_id,portfolio,effective_date" }
        );
      }
      await snapshotMmFundDurations("trade", inserted?.id ?? null);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ba-mm-trades"] });
      queryClient.invalidateQueries({ queryKey: ["ba-mm-fund-weights-all"] });
      queryClient.invalidateQueries({ queryKey: ["ba-mm-duration-snapshots"] });
      // Held-status in the picker is derived from the weight rows this trade just wrote.
      queryClient.invalidateQueries({ queryKey: ["ba-cbam-funds-active"] });
      setShowAdd(false);
      toast.success("Trade logged");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Name lookup spans every fund so trades on since-exited funds still render a name.
  const fundMap = new Map(allFunds.map((f) => [f.id, f]));
  const selectedFund = fundMap.get(form.fund_id);
  const snapByTrade = new Map<string, number | null>();
  for (const s of durSnaps || []) {
    if (s.trade_id) snapByTrade.set(s.trade_id, s.weighted_avg_duration);
  }
  // Propagate snapshot duration to every trade sharing the same batch_id.
  const snapByBatch = new Map<string, number | null>();
  for (const trade of trades || []) {
    if (trade.batch_id && snapByTrade.has(trade.id)) {
      const v = snapByTrade.get(trade.id);
      if (v != null) snapByBatch.set(trade.batch_id, v);
    }
  }
  const durationFor = (t: Trade): number | null | undefined => {
    const direct = snapByTrade.get(t.id);
    if (direct != null) return direct;
    if (t.batch_id) return snapByBatch.get(t.batch_id);
    return direct;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">MM Trade Log</h1>
          <p className="text-sm text-muted-foreground">Record and review Money Markets portfolio trades</p>
        </div>
        <div className="flex items-center gap-2">
        <BatchTradeDialog
          tradesTable="ba_mm_trades"
          weightsTable="ba_mm_cbam_fund_weights"
          portfolios={[{ num: 1, label: "MM" }]}
          funds={funds}
          invalidateKeys={[["ba-mm-trades"], ["ba-mm-fund-weights-all"], ["ba-mm-duration-snapshots"], ["ba-cbam-funds-active"]]}
          onAfterSave={async (batchId) => {
            const { data: batchTrades } = await supabase
              .from("ba_mm_trades")
              .select("id")
              .eq("batch_id", batchId)
              .order("created_at", { ascending: true })
              .limit(1);
            const firstId = batchTrades?.[0]?.id ?? null;
            await snapshotMmFundDurations("trade", firstId);
          }}
        />
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> Log Trade</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Log New Trade</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Trade Date</Label><Input type="date" value={form.trade_date} onChange={(e) => setForm({ ...form, trade_date: e.target.value })} /></div>
                <div>
                  <Label>Direction</Label>
                  <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v as "BUY" | "SELL" })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="BUY">BUY</SelectItem><SelectItem value="SELL">SELL</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Fund</Label>
                <FundPicker value={form.fund_id} onValueChange={(v) => setForm({ ...form, fund_id: v })} funds={funds} />
                {selectedFund?.previouslyHeldInRange && (
                  <p className="text-xs text-amber-600 dark:text-amber-500">
                    Previously held in MM and sold out. A BUY re-opens the position from the trade date.
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Checkbox checked={form.portfolio_1_included} onCheckedChange={(v) => setForm({ ...form, portfolio_1_included: !!v })} />
                <span className="text-sm font-medium w-16">MM</span>
                <Input type="number" step="0.01" className="w-24 h-8 text-xs"
                  value={form.portfolio_1_pct}
                  onChange={(e) => setForm({ ...form, portfolio_1_pct: parseFloat(e.target.value) || 0 })}
                  disabled={!form.portfolio_1_included} />
                <span className="text-xs text-muted-foreground">%</span>
              </div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="h-20" /></div>
              <Button onClick={() => addTradeMutation.mutate()} disabled={!form.fund_id}>Save Trade</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Type</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Fund House</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Fund</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Fund Class</th>
                <th className="text-left p-3 font-medium text-muted-foreground">ISIN</th>
                <th className="text-center p-3 font-medium text-muted-foreground">Dir</th>
                <th className="text-center p-3 font-medium text-muted-foreground">MM</th>
                <th className="text-center p-3 font-medium text-muted-foreground">Wtd Avg Duration</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Notes</th>
              </tr>
            </thead>
            <tbody>
              {trades?.map((trade, idx) => {
                const f = fundMap.get(trade.fund_id);
                const wAvg = durationFor(trade);
                return (
                  <tr key={trade.id} className={cn("border-b", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                    <td className="p-3 text-xs">{new Date(trade.trade_date).toLocaleDateString("en-GB")}</td>
                    <td className="p-3 text-xs">{trade.trade_type ? <Badge variant="outline" className="text-[10px]">{tradeTypeLabel(trade.trade_type)}</Badge> : "—"}</td>
                    <td className="p-3 text-xs">{f?.fund_manager || "—"}</td>
                    <td className="p-3 text-xs font-medium">{f?.fund_name || "Unknown"}</td>
                    <td className="p-3 text-xs">{f?.fund_class || "—"}</td>
                    <td className="p-3 text-xs font-mono text-muted-foreground">{f?.isin || "—"}</td>
                    <td className="p-3 text-center"><Badge variant={tradeDirectionVariant(trade.direction)} title={tradeDirectionHint(trade.direction)} className="text-[10px]">{trade.direction}</Badge></td>
                    <td className="p-3 text-center text-xs">{trade.portfolio_1_included ? `${Number(trade.portfolio_1_pct).toFixed(2)}%` : "—"}</td>
                    <td className="p-3 text-center text-xs font-mono">{wAvg != null ? `${Number(wAvg).toFixed(2)} days` : "—"}</td>
                    <td className="p-3 text-xs text-muted-foreground max-w-[200px] truncate">{trade.notes || "—"}</td>
                  </tr>
                );
              })}
              {(!trades || trades.length === 0) && (
                <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">No trades logged yet.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
